import { Link } from "react-router-dom";
import { useGsap } from "@/hooks/useGsap";
import { gsap, ScrollTrigger, prefersReducedMotion, canAnimateHeavy } from "@/lib/motion";
import { WRAP } from "@/components/story/layout";
import { InterfaceArtifact, FlowArtifact, SystemArtifact } from "@/components/story/Artifacts";

/**
 * Moment 4: what we build, shown as three systems.
 *
 * IMPORTANT, AND DELIBERATE: none of these three entries names a client, quotes
 * a metric, or claims an outcome, because AUT labs has no published client work
 * to point at yet. Everything below is a true description of a kind of system.
 *
 * TO PUBLISH A REAL CASE STUDY: replace the matching object in PROJECTS with
 * the real one. The shape is:
 *
 *   {
 *     n:      "01",
 *     title:  the system, in the owner's words
 *     problem: one sentence on what was happening before
 *     built:  one sentence on what now exists
 *     result: one sentence, only if it is literally true and you can defend
 *             the number on a call. Leave it out rather than round it up.
 *     labels: three capability words
 *     Art:    the drawn artifact, or swap in <img src="/img/your-screenshot.webp">
 *   }
 *
 * Do not add a logo, a testimonial, or a percentage that has not been measured.
 */
const PROJECTS = [
  {
    n: "01",
    title: "Operations automation",
    problem:
      "A job arrives by phone, gets written on paper, typed into a sheet, and chased on WhatsApp until somebody remembers to invoice it.",
    built:
      "One system that takes the job in once, moves it through its stages, and tells the right person when it stalls.",
    labels: ["Discovery", "Automation", "Internal tools"],
    Art: FlowArtifact,
  },
  {
    n: "02",
    title: "AI on your own information",
    problem:
      "The answer exists. It is in a quotation from March, a specification PDF, and one person's head.",
    built:
      "An assistant that reads your own documents and records, answers in plain language, and shows where the answer came from.",
    labels: ["AI systems", "Search", "Product engineering"],
    Art: SystemArtifact,
  },
  {
    n: "03",
    title: "Custom ERP and CRM",
    problem:
      "Off-the-shelf software fits somebody else's business, so half the team keeps a private spreadsheet anyway.",
    built:
      "Software built around how the work actually runs, with permissions, approvals and an audit trail from day one.",
    labels: ["Strategy", "Interface", "Engineering"],
    Art: InterfaceArtifact,
  },
];

export default function WorkScene() {
  const scope = useGsap((ctx, el) => {
    if (prefersReducedMotion() || !canAnimateHeavy()) return;

    const cards = el.querySelectorAll("[data-card]");

    cards.forEach((card, i) => {
      /* The last card has nothing rising over it, so it never recedes. */
      if (i === cards.length - 1) return;
      try {
        ScrollTrigger.create({
          trigger: card,
          start: "top top+=96",
          end: "+=70%",
          scrub: 0.7,
          onUpdate: (self) => {
            const p = self.progress;
            gsap.set(card, {
              scale: 1 - p * 0.04,
              opacity: 1 - p * 0.45,
              xPercent: -p * 1.6,
            });
          },
        });
      } catch {
        // Static stacking is a perfectly good fallback.
      }
    });
  }, []);

  return (
    <section ref={scope} className="border-b border-ink" data-testid="section-work">
      <div className={`${WRAP} pt-20 md:pt-28`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end">
          <div className="lg:col-span-7">
            <p className="story-index">03 / What we build</p>
            <h2 className="display story-h2 mt-6 max-w-[13ch]">Built for the messy middle.</h2>
            <p className="mt-7 max-w-[42ch] story-lead text-ink/75">
              Not the pilot project, and not the enterprise rollout. The part in between,
              where the work is real and the tools were never designed for it.
            </p>
          </div>

          {/* The right half used to be empty. It now carries the index of the
              three stories below, which is content rather than decoration and
              tells the reader how far this section runs. */}
          <div className="lg:col-span-4 lg:col-start-9">
            <ol className="border-t border-ink">
              {PROJECTS.map(({ n, title, labels }) => (
                <li key={n} className="flex items-baseline gap-5 py-4 border-b border-ink/20">
                  <span className="story-index w-6 shrink-0">{n}</span>
                  <span className="min-w-0">
                    <span className="display block text-[22px] leading-[1.1]">{title}</span>
                    <span className="block mt-1.5 text-[12px] text-ink/65">{labels.join(" / ")}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      <div className={`${WRAP} pb-20 md:pb-28`}>
        <div className="mt-12 md:mt-16">
          {PROJECTS.map(({ n, title, problem, built, labels, Art }, i) => (
            <article
              key={n}
              data-card
              data-testid={`work-${n}`}
              className="lg:sticky lg:top-24 bg-paper border-t border-ink pt-9 md:pt-10 pb-10 md:pb-12 will-change-transform"
              style={{ zIndex: i + 1 }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6">
                <div className="lg:col-span-6">
                  <p className="story-index">{n}</p>
                  <h3 className="display text-[36px] md:text-[54px] leading-[0.98] tracking-[-0.025em] mt-4">{title}</h3>

                  <p className="mt-6 max-w-[42ch] story-lead text-ink/70">{problem}</p>
                  <p className="mt-4 max-w-[42ch] story-lead text-ink">
                    <b className="font-semibold">{built}</b>
                  </p>

                  <ul className="mt-6 flex flex-wrap gap-x-2.5 gap-y-2">
                    {labels.map((l) => (
                      <li key={l} className="eyebrow border border-ink/25 px-3 py-2">
                        {l}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="lg:col-span-5 lg:col-start-8">
                  <div className="work-frame">
                    <div className="work-frame__bar" aria-hidden="true">
                      <span className="work-frame__dot" />
                      <span className="work-frame__dot" />
                      <span className="work-frame__dot" />
                      <span className="work-frame__url">{title}</span>
                    </div>
                    <div className="p-6">
                      <Art />
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="border-t border-ink pt-8">
          <Link to="/what-we-build" className="ember-link text-ember-deep text-lg" data-testid="work-more">
            What we build, in detail →
          </Link>
        </div>
      </div>
    </section>
  );
}
